import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import RetryNotice from './RetryNotice';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import { downloadCSV, makeCSV } from '../../components/CustomTableWithPagination/csvExport';
import dayjs from 'dayjs';

// Each approval type has its own shape (leave and WFH grant requests have date ranges, regularization has check-in/out, compensatory
// has a single work date) - no single "one table for everything" view would read cleanly, unlike CRO Pending
// Tasks' stages which all share the same item shape.
const TYPE_COLUMNS = {
  'leave-rh': [
    { field: 'employeeName', headerName: 'Employee' },
    { field: 'departmentName', headerName: 'Department' },
    { field: 'leaveType', headerName: 'Leave Type' },
    { field: 'from_date', headerName: 'From' },
    { field: 'to_date', headerName: 'To' },
    { field: 'no_of_days', headerName: 'Days' },
    { field: 'hoursPending', headerName: 'Hours Pending' },
  ],
  'leave-hr': [
    { field: 'employeeName', headerName: 'Employee' },
    { field: 'departmentName', headerName: 'Department' },
    { field: 'leaveType', headerName: 'Leave Type' },
    { field: 'from_date', headerName: 'From' },
    { field: 'to_date', headerName: 'To' },
    { field: 'no_of_days', headerName: 'Days' },
    { field: 'hoursPending', headerName: 'Hours Pending' },
  ],
  'wfh-grant-rh': [
    { field: 'employeeName', headerName: 'Employee' },
    { field: 'departmentName', headerName: 'Department' },
    { field: 'leaveType', headerName: 'Request' },
    { field: 'from_date', headerName: 'From' },
    { field: 'to_date', headerName: 'To' },
    { field: 'no_of_days', headerName: 'Days' },
    { field: 'reason', headerName: 'Reason' },
    { field: 'hoursPending', headerName: 'Hours Pending' },
  ],
  'wfh-grant-hr': [
    { field: 'employeeName', headerName: 'Employee' },
    { field: 'departmentName', headerName: 'Department' },
    { field: 'leaveType', headerName: 'Request' },
    { field: 'from_date', headerName: 'From' },
    { field: 'to_date', headerName: 'To' },
    { field: 'no_of_days', headerName: 'Days' },
    { field: 'reason', headerName: 'Reason' },
    { field: 'hoursPending', headerName: 'Hours Pending' },
  ],
  'regularization-rh': [
    { field: 'employeeName', headerName: 'Employee' },
    { field: 'reg_date', headerName: 'Date' },
    { field: 'reg_check_in', headerName: 'Check-in' },
    { field: 'reg_check_out', headerName: 'Check-out' },
    { field: 'reg_reason', headerName: 'Reason' },
    { field: 'hoursPending', headerName: 'Hours Pending' },
  ],
  'regularization-hr': [
    { field: 'employeeName', headerName: 'Employee' },
    { field: 'reg_date', headerName: 'Date' },
    { field: 'reg_check_in', headerName: 'Check-in' },
    { field: 'reg_check_out', headerName: 'Check-out' },
    { field: 'reg_reason', headerName: 'Reason' },
    { field: 'hoursPending', headerName: 'Hours Pending' },
  ],
  compensatory: [
    { field: 'employeeName', headerName: 'Employee' },
    { field: 'comp_work_date', headerName: 'Work Date' },
    { field: 'hoursPending', headerName: 'Hours Pending' },
  ],
};

const exportType = (type, label, items) => {
  const csv = makeCSV({ headers: TYPE_COLUMNS[type], rows: items });
  downloadCSV(csv, `Pending_Approvals_${label.replace(/[^\w]+/g, '_')}_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
};

const TypeTable = ({ type, items }) => {
  const columns = TYPE_COLUMNS[type] || [];
  if (items.length === 0) return <Typography color="text.secondary">Nothing pending here right now, in your scope.</Typography>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
            {columns.map((c) => (
              <TableCell key={c.field}><strong>{c.headerName}</strong></TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i} hover>
              {columns.map((c) => (
                <TableCell key={c.field}>{item[c.field] ?? '-'}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Pending Approvals - leave and WFH grant requests (RH + HR stages), attendance regularization, and compensatory off, all currently
// waiting on someone's approval. New section, not in Requirement Document v1.0. No threshold/config - just
// counts, unlike CRO Pending Tasks. Viewer scope is server-side: a manager sees their whole reporting-
// hierarchy downline's pending items, HR/Top Management/Technical/Finance see everyone.
const PendingApprovalsDashboard = () => {
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get('type');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: 'warning-dashboard/pending-approvals-dashboard', method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js), so this status check is required -
        // a .catch alone would never fire and this would silently show "nothing pending" instead of the error.
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load Pending Approvals');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({ totalEvaluated: res?.data?.totalEvaluated || 0, warningCount: res?.data?.warningCount || 0 });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load Pending Approvals');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const focused = typeFilter ? rows.find((r) => r.key === typeFilter) : null;
  const allItemsCount = useMemo(() => rows.reduce((sum, r) => sum + r.count, 0), [rows]);

  return (
    <WarningPage
      title={focused ? `Pending Approvals - ${focused.label}` : 'Pending Approvals'}
      actions={
        <Box display="flex" gap={1}>
          {typeFilter && (
            <Button component={Link} to="/dashboard/warning-dashboard/pending-approvals" variant="text">View all types</Button>
          )}
          {focused && (
            <Button variant="outlined" onClick={() => exportType(focused.key, focused.label, focused.items)} disabled={focused.items.length === 0}>
              Export CSV
            </Button>
          )}
          <Button component={Link} to="/dashboard/warning-dashboard" variant="text">Back to Warning Dashboard</Button>
        </Box>
      }
    >
      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && typeFilter && !focused && (
        <Typography color="text.secondary">That approval type wasn't found.</Typography>
      )}

      {!loading && !error && focused && (
        <Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip size="small" color={focused.count > 0 ? 'error' : 'success'} label={`${focused.count} pending`} />
          </Box>
          <TypeTable type={focused.key} items={focused.items} />
        </Box>
      )}

      {!loading && !error && !typeFilter && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Leave, WFH grant requests, attendance regularization, and compensatory off requests currently waiting on someone's approval.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {summary.warningCount} of {summary.totalEvaluated} types have a backlog · {allItemsCount} pending in total
          </Typography>
          <Stack gap={1.5}>
            {rows.map((r) => (
              <Paper key={r.key} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                  {r.isWarning ? (
                    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main' }} />
                  ) : (
                    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
                  )}
                  <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 180 }}>{r.label}</Typography>
                  <Chip size="small" color={r.count > 0 ? 'error' : 'success'} label={`${r.count} pending`} />
                  {r.items.length > 0 && (
                    <Button size="small" variant="text" onClick={() => exportType(r.key, r.label, r.items)}>
                      Export CSV
                    </Button>
                  )}
                </Stack>
                <Box sx={{ mt: 1.5 }}>
                  <TypeTable type={r.key} items={r.items} />
                </Box>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </WarningPage>
  );
};

export default PendingApprovalsDashboard;
